// Hooks
import { useSelector } from "react-redux";
import usePhoto from "../usePhoto";
import useCustomers from "../useCustomers";
import { useHistory } from "react-router-dom";

// Actions
import LoadingAction from "@common/redux/actions/LoadingAction";
import LoginAction from "@common/redux/actions/LoginAction";
import PhotoAction from "@common/redux/actions/PhotoAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import CustomerReviewModalAction from "@common/redux/actions/CustomerReviewModalAction";
import CustomerInteractionAction from "@common/redux/actions/CustomerInteractionAction";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";

// Helpers
import { toggleStatusItem } from "./useReviewsFilters";
import { handleError } from "@common/components/helpers/error";
import { stripBase64Prefix } from "@common/components/helpers/image";
import {
  getDatePart,
  getSevenDaysInFuture,
} from "@common/components/helpers/datetime";

// API Clients
import CustomerClient from "@common/api/CustomerClient";
import useCustomerInteraction from "../useCustomerInteraction";
import { fetchInteractionOptions } from "../../helpers/CustomerInteraction/fetchInteractionOptions";
import { mapCustomersToOptions } from "../../helpers/Customer/customers";
import { mapStatusToCustomer } from "../../helpers/labels";

const useCustomerReviewModal = () => {
  const history = useHistory();
  const { reviewStatusCounts, reviewStatusIds } = useCustomers();

  const { photo } = usePhoto();

  const { methods } = useCustomerInteraction();

  const data = useSelector((state) =>
    CustomerReviewModalAction.getStatePath(state),
  );
  const {
    selectedCustomer,
    viewingForm,
    comments,
    customerStatusOptions,
    selectedCustomerStatus,
    selectedSummaryRecord,
    returnDate,
  } = data;

  const setCustomer = (data) => CustomerFormAction.dispatch(data);

  const setCustomerInteractionState = (data) =>
    CustomerInteractionAction.dispatch(data);

  const setCustomerReviewState = CustomerReviewAction.dispatch;
  const setCustomerReviewModalState = CustomerReviewModalAction.dispatch;

  const getTabTitle = (currentTab) => {
    switch (currentTab) {
      case 1:
        return "Interactions";
      case 2:
        return "Linked Sessions";
      case 3:
        return "Reviews";
      default:
        return "";
    }
  };

  const onRecordClick = (selectedRecord) => {
    setCustomerReviewState({
      selectedRecord,
      selectedReview: selectedRecord,
    });
    setCustomerReviewModalState({ selectedRecord });
  };

  const onStatusChange = (id) => {
    const toggled = toggleStatusItem(id, reviewStatusIds);
    const newStatusIds =
      toggled.length > 0 ? toggled : reviewStatusCounts.map((x) => x.key);
    setCustomerReviewModalState({
      reviewStatusIds: newStatusIds,
    });
  };

  const onCustomerDetailsClick = (isInReadMode = false) => {
    LoginAction.dispatch({ isInReadMode }).then(() => {
      setCustomerReviewModalState({ openCustomerDetailsModal: true });
    });
  };

  const onRowClick = (record) => CustomerReviewModalAction.open(record);

  const closeModal = () => {
    if (viewingForm) {
      CustomerReviewModalAction.dispatch({
        viewingForm: !viewingForm,
      });
    } else {
      LoginAction.dispatch({ isInReadMode: false });
      PhotoAction.reset();
      CustomerReviewModalAction.dispatch({
        selectedRecord: null,
        selectedCustomer: null,
      });
      CustomerReviewModalAction.close();
    }
  };

  const onSave = () => {
    const image = photo ? stripBase64Prefix(photo) : selectedCustomer.image;
    const payload = {
      ...selectedCustomer,
      comments,
      image,
      ...toPayload(selectedCustomerStatus),
    };
    LoadingAction.set(true).then(() =>
      CustomerClient.Update(payload)
        .then(() => {
          AlertModalAction.open("Customer updated!", "Success");
          setCustomerReviewModalState({ selectedCustomer: payload });
          if (selectedCustomerStatus?.value === "isTakingBreak") {
            const dateToSubmit =
              returnDate || getDatePart(getSevenDaysInFuture());
            setCustomerReviewModalState({
              selectedCustomer: payload,
              customerStatus: selectedCustomerStatus,
              returnDate: dateToSubmit,
            });
          } else {
            setCustomerReviewModalState({
              selectedCustomer: payload,
              customerStatus: selectedCustomerStatus,
            });
          }
        })
        .catch((error) => {
          handleError(error);
          setCustomerReviewModalState({ selectedCustomerStatus: null });
        })
        .finally(() => {
          LoadingAction.set(false);
          setCustomerReviewModalState({ openStatusChangeModal: false });
        }),
    );
  };

  const STATUS_KEYS = customerStatusOptions.map((option) => option.value);

  const toPayload = (selected) => {
    if (!selected) return null;
    return STATUS_KEYS.reduce((acc, key) => {
      acc[key] = key === selected.value;
      return acc;
    }, {});
  };

  const onCommentsChange = (event) =>
    setCustomerReviewModalState({ comments: event.target.value });

  const onCustomerStatusChange = (_, { name, value }) => {
    const selectedCustomerStatus = customerStatusOptions.find(
      (x) => x.value === value,
    );

    const updatedCustomer = mapStatusToCustomer({
      customerStatus: selectedCustomerStatus,
      customer: selectedSummaryRecord?.customer,
    });

    setCustomerReviewModalState({
      selectedCustomerStatus,
      openStatusChangeModal: true,
      selectedSummaryRecord: {
        ...selectedSummaryRecord,
        customer: updatedCustomer,
      },
    });
  };

  const name = selectedCustomer
    ? `${selectedCustomer.forename ?? ""} ${selectedCustomer.surname ?? ""}`
    : "Customer Interaction";

  const changeTab = (currentTab) => setCustomerReviewModalState({ currentTab });

  const toggleCustomerReviewModal = (value) =>
    setCustomerReviewModalState({ openCustomerReviewModal: value });

  const onNewInteractionClick = (customer) => {
    const parsedCustomer = mapCustomersToOptions([customer]);
    closeModal();
    setCustomer({ customers: parsedCustomer });
    setCustomerInteractionState({
      isCustomerVulnerable: true,
      hideCustomerSearch: true,
      customer: customer,
      customerId: customer?.id,
      searchedCustomer: customer?.id,
    });
    fetchInteractionOptions();
    history.push("/compliance/new-customer-interaction");
  };

  return {
    ...data,
    getTabTitle,
    reviewStatusIds,
    methods,
    setCustomerReviewModalState,
    onStatusChange,
    onRowClick,
    onRecordClick,
    closeModal,
    onCustomerDetailsClick,
    name,
    onSave,
    onCommentsChange,
    onCustomerStatusChange,
    changeTab,
    toggleCustomerReviewModal,
    onNewInteractionClick,
  };
};

export default useCustomerReviewModal;
